import { useState, useEffect, useCallback, useRef } from 'react';
import { getQueueSize, processSyncQueue, syncOnStartupIfNeeded, getUnresolvedConflicts } from '@/lib/syncQueue';
import { replayOfflineLogs, getOfflineLogCount } from '@/lib/accessLogs';

export type SyncStatus = 'idle' | 'syncing' | 'error' | 'success';

interface OnlineStatusValue {
  isOnline: boolean;
  isSyncing: boolean;
  syncStatus: SyncStatus;
  pendingChanges: number;
  lastSyncTime: number | null;
  triggerSync: () => Promise<void>;
  wasOffline: boolean;
}

// Endpoint dédié pour le heartbeat — le SW ne le matche dans aucune règle
// de cache, et le paramètre nc (cache-busting) + cache: 'no-store'
// garantissent qu'aucun cache (navigateur ou SW) ne peut servir cette requête.
const CHECK_URL = '/favicon.ico';

export function useOnlineStatus(): OnlineStatusValue {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [pendingChanges, setPendingChanges] = useState(getQueueSize());
  const [lastSyncTime, setLastSyncTime] = useState<number | null>(null);
  const [wasOffline, setWasOffline] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wasOfflineConsumedRef = useRef(false);
  const isPageVisibleRef = useRef(true);

  const checkConnectivity = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      // Cache-busting dynamique : chaque URL est unique → le SW ne peut pas
      // la servir depuis son cache dynamique (la clé change à chaque appel).
      // + cache: 'no-store' empêche le cache navigateur.
      const checkUrl = `${CHECK_URL}?nc=${Date.now()}`;
      await fetch(checkUrl, {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return true;
    } catch {
      return false;
    }
  }, []);

  const triggerSync = useCallback(async () => {
    if (isSyncing || !isOnline) return;

    setIsSyncing(true);
    setSyncStatus('syncing');

    try {
      // 1. Rejouer les logs d'accès offline (conformité — timestamp réel préservé)
      if (getOfflineLogCount() > 0) {
        await replayOfflineLogs();
      }

      // 2. Rejouer les mutations de données
      const result = await processSyncQueue();
      setPendingChanges(result.remaining);

      if (result.failed > 0) {
        setSyncStatus('error');
      } else {
        setSyncStatus('success');
        setLastSyncTime(Date.now());
      }
    } catch {
      setSyncStatus('error');
    } finally {
      setIsSyncing(false);

      // Ne PAS réinitialiser à 'idle' si des conflits persistent —
      // l'utilisateur doit voir qu'il y a des problèmes à résoudre.
      const pendingConflicts = getUnresolvedConflicts().length;
      if (pendingConflicts === 0) {
        setTimeout(() => {
          setSyncStatus((prev) => {
            // Vérification de sûreté : si des conflits sont apparus entre-temps
            if (getUnresolvedConflicts().length > 0) return prev;
            return 'idle';
          });
        }, 3000);
      }
      // Si conflits > 0, on laisse le statut à 'error' ou 'success'
      // pour que l'UI garde la bannière visible.
    }
  }, [isSyncing, isOnline]);

  // Marquer wasOffline comme consommé après qu'il a été lu
  const consumeWasOffline = useCallback(() => {
    if (wasOffline && !wasOfflineConsumedRef.current) {
      wasOfflineConsumedRef.current = true;
      // On garde wasOffline = true pendant 5 secondes pour que
      // les composants aient le temps de l'afficher, puis on reset.
      setTimeout(() => {
        setWasOffline(false);
        wasOfflineConsumedRef.current = false;
      }, 5000);
    }
  }, [wasOffline]);

  // Détection online/offline via les événements navigateur
  useEffect(() => {
    const handleOnline = async () => {
      const actuallyOnline = await checkConnectivity();
      if (actuallyOnline) {
        setWasOffline(true);
        wasOfflineConsumedRef.current = false;
        setIsOnline(true);
        setPendingChanges(getQueueSize());

        // Synchro automatique au retour de connexion
        setTimeout(() => {
          triggerSync();
        }, 1500);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Vérification périodique toutes les 30 secondes
    // Suspendue quand la page est en arrière-plan (Page Visibility API)
    intervalRef.current = setInterval(async () => {
      if (!isPageVisibleRef.current) return; // Skip si onglet caché

      const actuallyOnline = await checkConnectivity();
      setIsOnline((prev) => {
        if (!prev && actuallyOnline) {
          setWasOffline(true);
          wasOfflineConsumedRef.current = false;
          setPendingChanges(getQueueSize());
          setTimeout(() => triggerSync(), 1000);
        }
        return actuallyOnline;
      });
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkConnectivity, triggerSync]);

  // Page Visibility API — suspendre le polling en arrière-plan
  useEffect(() => {
    const handleVisibilityChange = () => {
      isPageVisibleRef.current = !document.hidden;

      // Au retour au premier plan, vérifier immédiatement la connectivité
      // et rafraîchir pendingChanges
      if (!document.hidden) {
        checkConnectivity().then((online) => {
          setIsOnline((prev) => {
            if (!prev && online) {
              setWasOffline(true);
              wasOfflineConsumedRef.current = false;
              setPendingChanges(getQueueSize());
              setTimeout(() => triggerSync(), 1000);
            }
            return online;
          });
        });
        setPendingChanges(getQueueSize());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkConnectivity, triggerSync]);

  // Mettre à jour pendingChanges périodiquement
  // Suspendu quand la page est en arrière-plan
  useEffect(() => {
    const updatePending = setInterval(() => {
      if (isPageVisibleRef.current) {
        setPendingChanges(getQueueSize());
      }
    }, 5000);

    return () => clearInterval(updatePending);
  }, []);

  // Écouter l'événement de sync en arrière-plan du Service Worker
  useEffect(() => {
    const handleBackgroundSync = () => {
      if (navigator.onLine) {
        triggerSync();
      }
    };

    window.addEventListener('csf-background-sync', handleBackgroundSync);
    return () => window.removeEventListener('csf-background-sync', handleBackgroundSync);
  }, [triggerSync]);

  // Écouter le BroadcastChannel de coordination multi-onglets
  // Quand un autre onglet termine une synchro, on rafraîchit nos données
  useEffect(() => {
    const SYNC_CHANNEL = typeof BroadcastChannel !== 'undefined'
      ? new BroadcastChannel('csf-sync-coordination')
      : null;

    if (!SYNC_CHANNEL) return;

    const handleSyncMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SYNC_COMPLETED') {
        // Un autre onglet vient de terminer une synchro
        setPendingChanges(event.data.remaining || 0);

        if (event.data.conflictCount > 0) {
          setSyncStatus('error');
        } else if (event.data.success > 0) {
          setSyncStatus('success');
          setLastSyncTime(Date.now());
        }

        // Si des conflits sont apparus, ne pas reset à idle
        if (!event.data.conflictCount) {
          setTimeout(() => {
            setSyncStatus((prev) => {
              if (getUnresolvedConflicts().length > 0) return prev;
              return 'idle';
            });
          }, 3000);
        }
      }
    };

    SYNC_CHANNEL.addEventListener('message', handleSyncMessage);

    return () => {
      SYNC_CHANNEL.removeEventListener('message', handleSyncMessage);
      SYNC_CHANNEL.close();
    };
  }, []);

  // Déclencher la synchro au démarrage si des mutations sont en attente
  useEffect(() => {
    syncOnStartupIfNeeded();
  }, []);

  // Consommer wasOffline automatiquement quand l'utilisateur est de retour
  useEffect(() => {
    if (wasOffline && isOnline) {
      consumeWasOffline();
    }
  }, [wasOffline, isOnline, consumeWasOffline]);

  return {
    isOnline,
    isSyncing,
    syncStatus,
    pendingChanges,
    lastSyncTime,
    triggerSync,
    wasOffline,
  };
}